/**
 * Google Places Autocomplete helper.
 * Uses the Maps JavaScript API (AutocompleteService + PlacesService).
 * The SDK is loaded async via index.html <script> tag.
 */

let autocompleteService = null
let placesService = null
let sessionToken = null

/** Wait for the Google Maps SDK to load, then init services */
function ensureServices() {
  if (autocompleteService && placesService) return true
  if (!window.google?.maps?.places) return false
  autocompleteService = new window.google.maps.places.AutocompleteService()
  // PlacesService needs a DOM node (hidden) or map instance
  const div = document.createElement('div')
  placesService = new window.google.maps.places.PlacesService(div)
  return true
}

/** Create a fresh session token (groups autocomplete + details into one billing session) */
export function newSessionToken() {
  if (!window.google?.maps?.places) return null
  sessionToken = new window.google.maps.places.AutocompleteSessionToken()
  return sessionToken
}

/**
 * Fetch address predictions from Google Places Autocomplete.
 * @param {string} query - User input text
 * @returns {Promise<Array<{id, label, secondary, placeId}>>}
 */
export async function fetchAddressSuggestions(query) {
  if (!query?.trim()) return []
  if (!ensureServices()) return []

  if (!sessionToken) newSessionToken()

  return new Promise((resolve) => {
    autocompleteService.getPlacePredictions(
      {
        input: query,
        types: ['address'],
        componentRestrictions: { country: 'us' },
        sessionToken,
      },
      (predictions, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
          resolve([])
          return
        }
        const suggestions = predictions.map((p) => ({
          id: p.place_id,
          placeId: p.place_id,
          label: p.structured_formatting?.main_text || p.description,
          secondary: p.structured_formatting?.secondary_text || '',
          value: p.description,
        }))
        resolve(suggestions)
      }
    )
  })
}

/**
 * Resolve a place ID to full address + lat/lng via Place Details.
 * Consumes the session token (billing optimization).
 * @param {string} placeId
 * @returns {Promise<{address: string, lat: number, lng: number, zipCode: string} | null>}
 */
export async function getPlaceDetails(placeId) {
  if (!placeId || !ensureServices()) return null

  return new Promise((resolve) => {
    placesService.getDetails(
      {
        placeId,
        fields: ['formatted_address', 'geometry', 'address_components'],
        sessionToken,
      },
      (place, status) => {
        // Reset session token after details call (billing session complete)
        sessionToken = null

        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          resolve(null)
          return
        }

        const lat = place.geometry?.location?.lat()
        const lng = place.geometry?.location?.lng()
        const address = place.formatted_address || ''

        // Extract zip code from address components
        let zipCode = ''
        if (place.address_components) {
          const zipComponent = place.address_components.find((c) =>
            c.types.includes('postal_code')
          )
          if (zipComponent) zipCode = zipComponent.short_name
        }

        resolve({ address, lat, lng, zipCode })
      }
    )
  })
}
